import { useEffect, useMemo, useState, useCallback } from 'react'
import { visitServices } from '@/services/visit-services'
import { UsageLogModal } from '../components/usage-log-modal'
import { HeaderBar } from './header-bar'
import { ActiveCard } from './active-card'
import { ConfirmCancelModal } from './confirm-cancel-modal'
import { fmt, norm } from './utils'

export function VisitActive() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [q, setQ] = useState('')

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmTarget, setConfirmTarget] = useState(null)
  const [confirmBusy, setConfirmBusy] = useState(false)

  const [usageOpen, setUsageOpen] = useState(false)
  const [usageTarget, setUsageTarget] = useState(null) // { usage, title, visitor }

  const refresh = useCallback(async () => {
    setError(''); setLoading(true)
    try {
      const list = await visitServices.listApprovedActive()
      setRows(Array.isArray(list) ? list : [])
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Error al cargar vigentes')
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { refresh() }, [refresh])

  const filtered = useMemo(() => {
    const s = norm(q)
    if (!s) return rows
    return rows.filter(r => {
      const vd = r?.visitor_data || {}
      return [vd.name, vd.token_name, vd.email, vd.document_number, vd.host_person, r.office_name]
        .some(x => norm(x).includes(s))
    })
  }, [rows, q])

  const openConfirm = (inv) => { setConfirmTarget(inv); setConfirmOpen(true) }
  const closeConfirm = () => { if (!confirmBusy) { setConfirmOpen(false); setConfirmTarget(null) } }

  const openUsageModal = (inv) => {
    const usage = inv?.usage_data
    if (!usage || !Array.isArray(usage.events) || usage.events.length === 0) return
    const vd = inv?.visitor_data || {}
    setUsageTarget({ usage, title: `Uso de invitación — ${vd.name || 'Invitado'}`, visitor: vd })
    setUsageOpen(true)
  }
  const closeUsageModal = () => { setUsageOpen(false); setUsageTarget(null) }

  const confirmCancel = async () => {
    if (!confirmTarget) return
    setConfirmBusy(true); setError('')
    try {
      await visitServices.rejectInvitation(confirmTarget.id)
      setConfirmBusy(false); setConfirmOpen(false); setConfirmTarget(null)
      await refresh()
    } catch (e) {
      setConfirmBusy(false)
      setError(e?.response?.data?.error || e.message || 'No se pudo cancelar la visita')
    }
  }

  return (
    <div className="space-y-3 w-full">
      <HeaderBar loading={loading} count={filtered.length} q={q} setQ={setQ} onRefresh={refresh} />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3">{error}</div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-gray-600">Cargando…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 text-gray-700 rounded-lg p-3">
          No hay visitas vigentes.
        </div>
      ) : (
        <div className="space-y-3 w-full">
          {filtered.map(inv => (
            <ActiveCard
              key={inv.id}
              inv={inv}
              fmt={fmt}
              onOpenUsage={openUsageModal}
              onRequestCancel={openConfirm}
            />
          ))}
        </div>
      )}

      <ConfirmCancelModal
        open={confirmOpen}
        busy={confirmBusy}
        target={confirmTarget}
        onClose={closeConfirm}
        onConfirm={confirmCancel}
        fmt={fmt}
      />

      <UsageLogModal
        open={usageOpen}
        onClose={closeUsageModal}
        usage={usageTarget?.usage}
        title={usageTarget?.title}
        visitor={usageTarget?.visitor}
      />
    </div>
  )
}

export default VisitActive